import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fade, IconButton } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { BRAND_ORANGE } from '../ManageEvent/components/manageEventFieldStyles';

const SCROLL_END_THRESHOLD = 8;
const HINT_FADE_MS = 220;

const ScrollHintButton = ({ direction, visible, onClick }) => {
  const isDown = direction === 'down';
  const Icon = isDown ? ArrowDownwardIcon : ArrowUpwardIcon;

  return (
    <Fade in={visible} timeout={{ enter: HINT_FADE_MS, exit: HINT_FADE_MS }} unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          ...(isDown ? { bottom: 8 } : { top: 8 }),
        }}
      >
        <IconButton
          type="button"
          size="small"
          onClick={onClick}
          aria-label={isDown ? 'Scroll to bottom' : 'Scroll to top'}
          sx={{
            width: 28,
            height: 28,
            bgcolor: BRAND_ORANGE,
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease',
            '&:hover': {
              bgcolor: BRAND_ORANGE,
              filter: 'brightness(0.92)',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
            },
            '&:active': {
              transform: 'scale(0.94)',
            },
          }}
        >
          <Icon sx={{ fontSize: 18, color: '#fff' }} />
        </IconButton>
      </Box>
    </Fade>
  );
};

const ScrollableContentWithHint = ({ children, sx = {} }) => {
  const scrollRef = useRef(null);
  const hintDirectionRef = useRef(null);
  const resizeRafRef = useRef(0);
  const [hintDirection, setHintDirection] = useState(null);

  const updateHint = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const canScroll = element.scrollHeight > element.clientHeight + 1;
    const nextDirection = !canScroll
      ? null
      : element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_END_THRESHOLD
        ? 'up'
        : 'down';

    if (hintDirectionRef.current === nextDirection) {
      return;
    }

    hintDirectionRef.current = nextDirection;
    setHintDirection(nextDirection);
  }, []);

  const scheduleHintUpdate = useCallback(() => {
    if (resizeRafRef.current) {
      cancelAnimationFrame(resizeRafRef.current);
    }

    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = 0;
      updateHint();
    });
  }, [updateHint]);

  const scrollToEdge = useCallback((direction) => {
    const element = scrollRef.current;
    if (!element) return;

    element.scrollTo({
      top: direction === 'down' ? element.scrollHeight : 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    updateHint();

    const resizeObserver = new ResizeObserver(scheduleHintUpdate);
    resizeObserver.observe(element);

    const contentElement = element.firstElementChild;
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }

    return () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [children, scheduleHintUpdate, updateHint]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={scrollRef}
        onScroll={updateHint}
        sx={{
          scrollBehavior: 'smooth',
          ...sx,
        }}
      >
        {children}
      </Box>

      <ScrollHintButton
        direction="down"
        visible={hintDirection === 'down'}
        onClick={() => scrollToEdge('down')}
      />
      <ScrollHintButton
        direction="up"
        visible={hintDirection === 'up'}
        onClick={() => scrollToEdge('up')}
      />
    </Box>
  );
};

export default ScrollableContentWithHint;
